export const ROLE_LAYERS = { cut: 'CUT', bend: 'BEND', engrave: 'ENGRAVE', info: 'INFO' };

const number = value => Number.isFinite(Number(value)) ? Number(value) : 0;
const pointKey = (point, tolerance = 0.35) => `${Math.round(number(point.x) / tolerance)}:${Math.round(number(point.y) / tolerance)}`;

export function entityPoints(entity, arcSteps = 48) {
  if (!entity) return [];
  if (entity.type === 'line') return [{x:number(entity.x1),y:number(entity.y1)},{x:number(entity.x2),y:number(entity.y2)}];
  if (entity.type === 'circle') {
    return Array.from({length:arcSteps},(_,i)=>{
      const angle=2*Math.PI*i/arcSteps;
      return {x:number(entity.cx)+Math.cos(angle)*number(entity.r),y:number(entity.cy)+Math.sin(angle)*number(entity.r)};
    });
  }
  if (entity.type === 'arc') {
    let start=number(entity.start),end=number(entity.end);
    while(end<start)end+=360;
    const steps=Math.max(8,Math.ceil(Math.abs(end-start)/5));
    return Array.from({length:steps+1},(_,i)=>{
      const angle=(start+(end-start)*i/steps)*Math.PI/180;
      return {x:number(entity.cx)+Math.cos(angle)*number(entity.r),y:number(entity.cy)+Math.sin(angle)*number(entity.r)};
    });
  }
  return (entity.points||[]).map(point=>({x:number(point.x),y:number(point.y)}));
}

export function entityLength(entity) {
  if (entity.type === 'line') return Math.hypot(number(entity.x2)-number(entity.x1),number(entity.y2)-number(entity.y1));
  if (entity.type === 'circle') return 2*Math.PI*Math.abs(number(entity.r));
  if (entity.type === 'arc') {
    let sweep=number(entity.end)-number(entity.start); while(sweep<0)sweep+=360;
    return Math.abs(sweep)*Math.PI/180*Math.abs(number(entity.r));
  }
  const points=entityPoints(entity);
  let length=points.slice(1).reduce((sum,p,i)=>sum+Math.hypot(p.x-points[i].x,p.y-points[i].y),0);
  if(entity.closed&&points.length>2)length+=Math.hypot(points[0].x-points.at(-1).x,points[0].y-points.at(-1).y);
  return length;
}

export function entityEndpoints(entity) {
  if (entity.type === 'circle' || entity.closed) return [];
  const points=entityPoints(entity);
  return points.length>1?[points[0],points.at(-1)]:[];
}

export function previewShapeForEntity(entity) {
  return entity?.closed ? 'polygon' : 'polyline';
}

export function roleFor(entity) {
  const role=String(entity?.role||'cut').toLowerCase();
  return ['cut','bend','engrave','info'].includes(role)?role:'cut';
}

export function withRole(entity, role) {
  const next=roleFor({role});
  return {...entity,role:next,layer:ROLE_LAYERS[next],manualWizardEdit:true};
}

export function boundsForEntities(entities=[]) {
  const points=entities.flatMap(entity=>entityPoints(entity));
  if(!points.length)return {minX:0,maxX:1,minY:0,maxY:1,width:1,height:1};
  const minX=Math.min(...points.map(p=>p.x)),maxX=Math.max(...points.map(p=>p.x));
  const minY=Math.min(...points.map(p=>p.y)),maxY=Math.max(...points.map(p=>p.y));
  return {minX,maxX,minY,maxY,width:Math.max(1,maxX-minX),height:Math.max(1,maxY-minY)};
}

export function deriveGeometry(base={}, entities=base.entities||[]) {
  const normalised=entities.map(entity=>({...entity,lengthMm:entityLength(entity)}));
  const bounds=boundsForEntities(normalised);
  const cut=normalised.filter(entity=>roleFor(entity)==='cut');
  const engrave=normalised.filter(entity=>roleFor(entity)==='engrave');
  const layerMap=new Map();
  normalised.forEach(entity=>{
    const key=`${entity.layer||ROLE_LAYERS[roleFor(entity)]}\u0000${entity.colour||''}`;
    const current=layerMap.get(key)||{layer:entity.layer||ROLE_LAYERS[roleFor(entity)],colour:entity.colour||'',count:0};
    current.count+=1; layerMap.set(key,current);
  });
  return {...base,
    ...(entities!==base.entities?{areaSqm:null,areaMeasurement:'unavailable_after_edit'}:{}),
    widthMm:bounds.width,heightMm:bounds.height,bounds:{minX:bounds.minX,maxX:bounds.maxX,minY:bounds.minY,maxY:bounds.maxY},
    preview:`${Math.round(bounds.width)} x ${Math.round(bounds.height)} mm`,entities:normalised,
    cuttingLengthM:cut.reduce((sum,e)=>sum+e.lengthMm,0)/1000,
    engravingLengthM:engrave.reduce((sum,e)=>sum+e.lengthMm,0)/1000,
    pierceCount:cut.filter(e=>e.type==='circle'||e.closed).length||Math.min(1,cut.length),
    layerSummary:Array.from(layerMap.values()),source:'DXF',parserContract:'smartquote-v1-native-dxf'
  };
}

const dxfPair=(code,value)=>`${code}\n${value}\n`;
function writeEntity(entity) {
  const layer=entity.layer||ROLE_LAYERS[roleFor(entity)];
  const colour=String(entity.colour??'').trim();
  let out='';
  if(entity.type==='line')out=dxfPair(0,'LINE')+dxfPair(8,layer)+dxfPair(10,number(entity.x1))+dxfPair(20,number(entity.y1))+dxfPair(11,number(entity.x2))+dxfPair(21,number(entity.y2));
  else if(entity.type==='circle')out=dxfPair(0,'CIRCLE')+dxfPair(8,layer)+dxfPair(10,number(entity.cx))+dxfPair(20,number(entity.cy))+dxfPair(40,Math.abs(number(entity.r)));
  else if(entity.type==='arc')out=dxfPair(0,'ARC')+dxfPair(8,layer)+dxfPair(10,number(entity.cx))+dxfPair(20,number(entity.cy))+dxfPair(40,Math.abs(number(entity.r)))+dxfPair(50,number(entity.start))+dxfPair(51,number(entity.end));
  else {
    const points=entityPoints(entity);
    if(points.length<2)return '';
    out=dxfPair(0,'LWPOLYLINE')+dxfPair(8,layer)+dxfPair(90,points.length)+dxfPair(70,entity.closed?1:0);
    points.forEach(point=>{out+=dxfPair(10,point.x)+dxfPair(20,point.y);});
  }
  if(colour)out+=dxfPair(62,colour);
  return out;
}

export function geometryToDxf(geometry) {
  const entities=(geometry?.entities||[]).map(writeEntity).join('');
  return dxfPair(0,'SECTION')+dxfPair(2,'HEADER')+dxfPair(0,'ENDSEC')+dxfPair(0,'SECTION')+dxfPair(2,'ENTITIES')+entities+dxfPair(0,'ENDSEC')+dxfPair(0,'EOF').trimEnd();
}

export function scaleGeometry(geometry, factor) {
  const value=Number(factor);
  if(!Number.isFinite(value)||value<=0)throw new Error('Scale factor must be greater than zero.');
  const scalePoint=p=>({...p,x:number(p.x)*value,y:number(p.y)*value});
  const entities=(geometry.entities||[]).map(entity=>{
    const next={...entity};
    ['x1','y1','x2','y2','cx','cy','r'].forEach(key=>{if(next[key]!=null)next[key]=number(next[key])*value;});
    if(next.points)next.points=next.points.map(scalePoint);
    return next;
  });
  return deriveGeometry(geometry,entities);
}

function signature(entity,tolerance=.001) {
  const round=v=>Math.round(number(v)/tolerance);
  return JSON.stringify({type:entity.type,role:roleFor(entity),closed:!!entity.closed,points:entityPoints(entity).map(p=>[round(p.x),round(p.y)])});
}

export function cleanGeometry(geometry, settings={}) {
  let entities=[...(geometry.entities||[])];
  const notes=[];
  if(settings.removeDuplicates){const seen=new Set();const before=entities.length;entities=entities.filter(entity=>{const key=signature(entity);if(seen.has(key))return false;seen.add(key);return true;});if(before!==entities.length)notes.push(`${before-entities.length} duplicate entities removed`);}
  if(settings.simplifySmallEntities){const tolerance=Math.max(0,number(settings.simplifyToleranceMm));const before=entities.length;entities=entities.filter(entity=>entityLength(entity)>tolerance);if(before!==entities.length)notes.push(`${before-entities.length} tiny entities removed`);}
  if(settings.removeInfoLayers){const before=entities.length;entities=entities.filter(entity=>roleFor(entity)!=='info');if(before!==entities.length)notes.push(`${before-entities.length} info entities removed`);}
  if(settings.closeOpenContours){
    const tolerance=Math.max(0,number(settings.closeToleranceMm));
    const lines=entities.map((entity,index)=>({entity,index,ends:entityEndpoints(entity)})).filter(item=>item.ends.length===2);
    const replacements=new Map();
    for(let a=0;a<lines.length;a+=1)for(let b=a+1;b<lines.length;b+=1){
      for(let ai=0;ai<2;ai+=1)for(let bi=0;bi<2;bi+=1){const p=lines[a].ends[ai],q=lines[b].ends[bi],distance=Math.hypot(p.x-q.x,p.y-q.y);if(distance>0&&distance<=tolerance){const mid={x:(p.x+q.x)/2,y:(p.y+q.y)/2};replacements.set(`${lines[a].index}:${ai}`,mid);replacements.set(`${lines[b].index}:${bi}`,mid);}}
    }
    if(replacements.size){entities=entities.map((entity,index)=>{if(entity.type==='line'){const a=replacements.get(`${index}:0`),b=replacements.get(`${index}:1`);return {...entity,...(a?{x1:a.x,y1:a.y}:{}),...(b?{x2:b.x,y2:b.y}:{})};}if(entity.type==='polyline'&&!entity.closed){const points=[...(entity.points||[])];const a=replacements.get(`${index}:0`),b=replacements.get(`${index}:1`);if(a&&points.length)points[0]=a;if(b&&points.length)points[points.length-1]=b;return {...entity,points};}return entity;});notes.push(`${replacements.size/2} close endpoint pairs joined`);}
  }
  return {...deriveGeometry(geometry,entities),cleanupMessage:notes.join('. ')};
}

export function connectedComponents(geometry,tolerance=.35) {
  const entities=geometry.entities||[];
  const endpointSets=entities.map(entity=>new Set(entityEndpoints(entity).map(point=>pointKey(point,tolerance))));
  const visited=new Set(),groups=[];
  for(let start=0;start<entities.length;start+=1){
    if(visited.has(start))continue;
    const queue=[start],group=[];visited.add(start);
    while(queue.length){const current=queue.shift();group.push(current);for(let other=0;other<entities.length;other+=1){if(visited.has(other))continue;const linked=[...endpointSets[current]].some(key=>endpointSets[other].has(key));if(linked){visited.add(other);queue.push(other);}}}
    groups.push(group);
  }
  return groups.map(indexes=>deriveGeometry(geometry,indexes.map(index=>entities[index])));
}

export function reviewIssues(geometry, tolerance=.35) {
  const entities=geometry?.entities||[];
  const checks=geometry?.drawingChecks?.checks||[];
  const descriptions=geometry?.warnings||[];
  const endpointOwners=new Map();
  entities.forEach((entity,index)=>{
    if(roleFor(entity)!=='cut')return;
    entityEndpoints(entity).forEach(point=>{
      const key=pointKey(point,tolerance),owners=endpointOwners.get(key)||[];
      owners.push(index);endpointOwners.set(key,owners);
    });
  });
  const openIndexes=[...new Set([...endpointOwners.values()].filter(owners=>owners.length%2===1).flat())];
  return checks.map((check,index)=>{
    const type=check.type;
    if(type==='open-contours')return {type,count:number(check.count),severity:'warning',title:'Open contour endpoints',description:descriptions.find(value=>/open contour/i.test(value))||'Cut paths contain unmatched endpoints.',help:'Highlight the affected geometry, repair or remove the open paths, then run the wizard again. Small gaps can be joined using Configure → Close small open gaps.',entityIndexes:openIndexes,action:'Highlight open paths'};
    if(type==='approximated-entities'){const entityIndexes=entities.map((entity,entityIndex)=>entity.approximated?entityIndex:-1).filter(value=>value>=0);return {type,count:number(check.count),severity:'warning',title:'Converted curved geometry',description:descriptions.find(value=>/converted for quoting preview/i.test(value))||'Curved source entities were converted to polylines.',help:'Highlight and inspect the converted curves. Accept the warning only when their displayed shape is correct.',entityIndexes,action:'Highlight converted curves'};}
    if(type==='unsupported-entities')return {type,count:number(check.count),severity:'warning',title:'Unsupported source entities',description:descriptions.find(value=>/unsupported entities/i.test(value))||'Some source entities could not be displayed.',help:'These entities were not imported and cannot be selected here. Re-export them from CAD as lines, arcs, circles or polylines, then reload the DXF.',entityIndexes:[],action:'Reload replacement DXF'};
    if(type==='no-cut-geometry'){const entityIndexes=entities.map((_entity,entityIndex)=>entityIndex);return {type,count:number(check.count),severity:'error',title:'No laser cut geometry',description:descriptions.find(value=>/no laser cut geometry/i.test(value))||'No supported geometry is classified as a cut path.',help:'Highlight the drawing and classify the required entities as Cut. This issue cannot be acknowledged.',entityIndexes,action:'Select geometry'};}
    return {type,count:number(check.count),severity:'warning',title:'Drawing check',description:descriptions[index]||'This drawing needs manual review.',help:'Inspect the drawing before accepting this warning.',entityIndexes:[],action:'Review drawing'};
  });
}

export function issueSummary(geometry) {
  const checks=geometry?.drawingChecks?.checks||[];
  const errors=checks.filter(check=>check.type==='no-cut-geometry').reduce((sum,item)=>sum+number(item.count),0);
  const warnings=Math.max(0,checks.reduce((sum,item)=>sum+number(item.count),0)-errors);
  return {errors,warnings,info:Object.values(geometry?.approximatedEntityCounts||{}).reduce((a,b)=>a+number(b),0),ready:errors===0&&warnings===0};
}
